import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../../App';
import { Upload, FileText, Download, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const BulkImportCard = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [groupeSites, setGroupeSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [wpStatus, setWpStatus] = useState('publish'); // 'draft' ou 'publish'
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [loadingSites, setLoadingSites] = useState(false);

  // Charger les sites groupe
  const loadGroupeSites = useCallback(async () => {
    if (user?.grade !== 'amiral') return;
    setLoadingSites(true);
    try {
      const { data } = await axios.get(`${API}/bulk-import/groupe-sites`);
      setGroupeSites(data.sites || []);
      if (data.sites?.length > 0) {
        setSelectedSite(data.sites[0].id);
      }
    } catch (error) {
      console.error('Error loading groupe sites:', error);
      toast.error('Erreur lors du chargement des sites groupe');
    } finally {
      setLoadingSites(false);
    }
  }, [user?.grade]);

  useEffect(() => {
    if (isOpen && groupeSites.length === 0) {
      loadGroupeSites();
    }
  }, [isOpen, groupeSites.length, loadGroupeSites]);

  // Ne pas afficher si pas amiral
  if (user?.grade !== 'amiral') {
    return null;
  }

  const handleFileUpload = async (event, format) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedSite) {
      toast.error('Veuillez sélectionner un site');
      return;
    }

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_id', selectedSite);
    formData.append('wp_status', wpStatus);

    try {
      const endpoint = format === 'csv' ? 'upload-csv' : 'upload-json';
      const { data } = await axios.post(`${API}/bulk-import/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(data);
      
      if (data.success) {
        const statusLabel = wpStatus === 'publish' ? 'publiés' : 'en brouillon';
        toast.success(`${data.imported} article(s) importé(s) et ${statusLabel} sur WordPress`);
      } else {
        toast.warning(`${data.imported}/${data.total} article(s) importé(s), ${data.errors.length} erreur(s)`);
      }
    } catch (error) {
      console.error('Import error:', error);
      const message = error.response?.data?.detail || 'Erreur lors de l\'import';
      toast.error(message);
      setImportResult({
        success: false,
        total: 0,
        imported: 0,
        errors: [{ error: message }]
      });
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const downloadTemplate = async (format) => {
    try {
      const { data } = await axios.get(`${API}/bulk-import/template-${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'import_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'import_template.json';
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Template ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Erreur lors du téléchargement du template');
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="bulk-import-card">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-800/30 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between text-orange-400">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import en masse (Sites Groupe)
          </div>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </CardTitle>
      </CardHeader>
      
      {isOpen && (
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-400">
            Importez des articles en masse et publiez-les directement sur WordPress. 
            Idéal pour créer des articles antidatés.
          </p>

          {/* Sélection du site */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Site cible
            </label>
            {loadingSites ? (
              <div className="text-slate-500 text-sm">Chargement des sites...</div>
            ) : groupeSites.length === 0 ? (
              <div className="text-amber-500 text-sm">
                Aucun site de catégorie "groupe" trouvé
              </div>
            ) : (
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                data-testid="bulk-import-site-select"
              >
                {groupeSites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Choix du statut WordPress */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Statut WordPress
            </label>
            <div className="flex gap-3">
              <label className={`
                flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all
                ${wpStatus === 'publish' 
                  ? 'bg-emerald-600/30 border-2 border-emerald-500 text-emerald-300' 
                  : 'bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600'}
              `}>
                <input
                  type="radio"
                  name="wpStatus"
                  value="publish"
                  checked={wpStatus === 'publish'}
                  onChange={(e) => setWpStatus(e.target.value)}
                  className="sr-only"
                />
                <CheckCircle className={`w-4 h-4 ${wpStatus === 'publish' ? 'text-emerald-400' : ''}`} />
                <span>Publié</span>
              </label>
              
              <label className={`
                flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all
                ${wpStatus === 'draft' 
                  ? 'bg-amber-600/30 border-2 border-amber-500 text-amber-300' 
                  : 'bg-slate-800 border border-slate-700 text-slate-400 hover:border-slate-600'}
              `}>
                <input
                  type="radio"
                  name="wpStatus"
                  value="draft"
                  checked={wpStatus === 'draft'}
                  onChange={(e) => setWpStatus(e.target.value)}
                  className="sr-only"
                />
                <FileText className={`w-4 h-4 ${wpStatus === 'draft' ? 'text-amber-400' : ''}`} />
                <span>Brouillon</span>
              </label>
            </div>
          </div>

          {/* Templates */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate('csv')}
              className="flex items-center gap-1 text-xs"
              data-testid="download-csv-template"
            >
              <Download className="w-3 h-3" />
              Template CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate('json')}
              className="flex items-center gap-1 text-xs"
              data-testid="download-json-template"
            >
              <Download className="w-3 h-3" />
              Template JSON
            </Button>
          </div>

          {/* Upload */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileUpload(e, 'csv')}
                  disabled={importing || !selectedSite}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                <div className={`
                  flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg
                  transition-colors cursor-pointer
                  ${importing || !selectedSite 
                    ? 'border-slate-700 bg-slate-800/30 cursor-not-allowed opacity-50' 
                    : 'border-slate-600 hover:border-orange-500/50 hover:bg-slate-800/50'}
                `}>
                  <FileText className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-300">Upload CSV</span>
                  <span className="text-xs text-slate-500">Séparateur: ; ou ,</span>
                </div>
              </label>
            </div>

            <div>
              <label className="block">
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => handleFileUpload(e, 'json')}
                  disabled={importing || !selectedSite}
                  className="hidden"
                  data-testid="json-file-input"
                />
                <div className={`
                  flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg
                  transition-colors cursor-pointer
                  ${importing || !selectedSite 
                    ? 'border-slate-700 bg-slate-800/30 cursor-not-allowed opacity-50' 
                    : 'border-slate-600 hover:border-orange-500/50 hover:bg-slate-800/50'}
                `}>
                  <FileText className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-300">Upload JSON</span>
                  <span className="text-xs text-slate-500">Format: {"{ articles: [...] }"}</span>
                </div>
              </label>
            </div>
          </div>

          {/* Status */}
          {importing && (
            <div className="flex items-center gap-2 text-orange-400 text-sm">
              <div className="animate-spin w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full" />
              Import et publication en cours...
            </div>
          )}

          {/* Résultat */}
          {importResult && (
            <div className={`p-3 rounded-lg ${importResult.success ? 'bg-emerald-900/30 border border-emerald-700' : 'bg-amber-900/30 border border-amber-700'}`}>
              <div className="flex items-center gap-2 mb-2">
                {importResult.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                )}
                <span className={importResult.success ? 'text-emerald-300' : 'text-amber-300'}>
                  {importResult.imported}/{importResult.total} article(s) importé(s)
                </span>
              </div>
              
              {importResult.published_count > 0 && (
                <div className="text-xs text-emerald-400 mb-1">
                  ✓ {importResult.published_count} publiés sur WordPress
                </div>
              )}
              {importResult.draft_count > 0 && (
                <div className="text-xs text-amber-400 mb-1">
                  ✓ {importResult.draft_count} en brouillon sur WordPress
                </div>
              )}
              
              {importResult.errors?.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-slate-400">Erreurs:</div>
                  {importResult.errors.slice(0, 5).map((err, idx) => (
                    <div key={idx} className="text-xs text-red-400">
                      • {err.title ? `${err.title}: ` : ''}{err.error}
                    </div>
                  ))}
                  {importResult.errors.length > 5 && (
                    <div className="text-xs text-slate-500">
                      ... et {importResult.errors.length - 5} autre(s) erreur(s)
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info format */}
          <div className="text-xs text-slate-500 space-y-1 bg-slate-800/50 p-3 rounded-lg">
            <p className="text-slate-400 font-medium mb-1">Format des fichiers :</p>
            <p><strong>Colonnes:</strong> title*, content*, summary, keywords, categories, seo_title, seo_description, created_at</p>
            <p><strong>Date (antidatage):</strong> YYYY-MM-DD ou DD/MM/YYYY</p>
            <p><strong>Contenu:</strong> HTML avec balises &lt;p&gt;, &lt;h2&gt;, etc.</p>
            <p className="text-slate-600">* champs obligatoires</p>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default BulkImportCard;
